"use client";

import React, { useEffect, useMemo, useState } from "react";
import {
  getDeviceFromNavigator,
  isSafari,
  DeviceOS,
} from "../../utils/client-device";

type Props = {
  appName: string;
  appIconUrl?: string;

  deepLinkUrl: string;

  iosAppStoreUrl: string;
  androidStoreUrl: string;

  androidPackage?: string;

  position?: "top" | "bottom";

  storageKey?: string;
  dismissForDays?: number;
};

const DISMISS_DEFAULT_KEY = "__smart_banner_dismissed";
const DISMISS_DEFAULT_DAYS = 30;

function daysFromNow(days: number) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.getTime();
}

export default function SmartAppBanner({
  appName,
  appIconUrl,
  deepLinkUrl,
  iosAppStoreUrl,
  androidStoreUrl,
  androidPackage,
  position = "bottom",
  storageKey = DISMISS_DEFAULT_KEY,
  dismissForDays = DISMISS_DEFAULT_DAYS,
}: Props) {
  const [visible, setVisible] = useState(false);
  const [os, setOs] = useState<DeviceOS>("other");

  const shouldShow = useMemo(() => {
    if (os === "android") return true;
    if (os === "ios" && !isSafari()) return true;
    return false;
  }, [os]);

  useEffect(() => {
    setOs(getDeviceFromNavigator());
  }, []);

  useEffect(() => {
    if (!shouldShow) {
      setVisible(false);
      return;
    }
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const until = Number(raw);
        if (Date.now() < until) return;
      }
      setVisible(true);
    } catch {
      setVisible(true);
    }
  }, [shouldShow, storageKey]);

  const handleDismiss = () => {
    try {
      localStorage.setItem(storageKey, String(daysFromNow(dismissForDays)));
    } catch {}
    setVisible(false);
  };

  const tryOpenOrStore = () => {
    const start = Date.now();

    const redirectToStore = () => {
      if (os === "android") {
        if (androidPackage) {
          const intentUrl = `intent://${deepLinkUrl.replace(
            /^[^:]+:\/\//,
            ""
          )}#Intent;scheme=${
            deepLinkUrl.split("://")[0]
          };package=${androidPackage};S.browser_fallback_url=${encodeURIComponent(
            androidStoreUrl
          )};end`;
          window.location.href = intentUrl;
          return;
        }
        window.location.href = androidStoreUrl;
        return;
      }
      window.location.href = iosAppStoreUrl;
    };

    window.location.href = deepLinkUrl;

    setTimeout(() => {
      const elapsed = Date.now() - start;
      if (document.visibilityState === "visible" && elapsed < 2500) {
        redirectToStore();
      }
    }, 1200);
  };

  if (!visible) return null;

  return (
    <div
      className={[
        "fixed inset-x-0 z-50",
        position === "top" ? "top-0" : "bottom-0",
      ].join(" ")}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          className={[
            "mb-3 sm:mb-4 rounded-2xl shadow-lg ring-1 ring-white/10 backdrop-blur",
            "bg-neutral-900/80 text-white",
            position === "top" ? "mt-3 sm:mt-4" : "mb-3 sm:mb-6",
          ].join(" ")}
        >
          <div className="flex items-center gap-3 p-3 sm:p-4">
            {appIconUrl ? (
              <img
                src={appIconUrl}
                alt={`${appName} icon`}
                className="w-10 rounded-xl flex-none"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="h-12 w-12 rounded-xl bg-white/10 flex-none" />
            )}

            <div className="flex-1 min-w-0">
              <p className="text-base font-semibold leading-tight truncate">
                {appName}
              </p>
              <p className="text-sm opacity-80 truncate">
                Get the app for a faster, native experience.
              </p>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={tryOpenOrStore}
                className="px-3 sm:px-4 py-2 rounded-xl bg-white text-neutral-900 text-sm font-semibold hover:bg-white/90 transition"
                aria-label="Open or install the app"
              >
                {os === "android" ? "Get it on Play" : "Get the app"}
              </button>

              <button
                onClick={handleDismiss}
                className="px-3 py-2 rounded-xl bg-neutral-800 text-white/90 text-sm hover:bg-neutral-700 transition"
                aria-label="Dismiss"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
